/**
 * The Map Review workbench state the two persistence tiers share.
 *
 * Two places remember how the workbench was left: the URL (`@/lib/mapReviewUrl`,
 * authoritative whenever it carries `state=1`) and the browser's storage
 * (`@/lib/mapReviewPrefs`, the fallback a bare visit restores from). Both decode
 * the SAME shape — the store's `MapReviewSpec` and the active table, page, limit,
 * sort and ledger flags — so the validation lives here rather than in either
 * tier: one parser means a URL and a stored analysis can never disagree about
 * what a spec is, and a rule corrected once is corrected for both.
 *
 * The module is pure: it imports no Vue, no router and no storage, so it is
 * unit-testable on its own and neither tier has to know about the other.
 */

import type { GeoJSON } from "geojson";

import type {
	MapReviewColumnFilter,
	MapReviewFilterOp,
	MapReviewSort,
	MapReviewSpec,
	MapReviewTable,
	MapReviewTempZone,
} from "@/type/api";

/**
 * The persisted state's schema version. It exists because the column registry
 * (`@/components/map-review/columns`) changes over time: a stored filter naming
 * a column or operator the server no longer accepts would 400 on load. The
 * structural validation below checks only that an op is one the wire knows, not
 * that the filter's column offers it, so any change to a column's `filterOps`
 * MUST bump this — a stored spec from before the change is discarded rather
 * than restored. Version 2: the Applicator (creator) column moved from a text
 * `contains` filter to an enum of user ids.
 */
export const MAP_REVIEW_ANALYSIS_VERSION = 2;

// The ledger's tables. Kept local so this lib module never reaches into the UI
// layer; a restored state is validated against these, so a stored active table
// or filter always names one of the surfaces the server resolves. A surface
// added after a state was stored would be appended to the list, so that state
// still restores rather than being discarded.
export const MAP_REVIEW_TABLES: readonly MapReviewTable[] = [
	"site",
	"feature",
	"treatment",
	"observation",
	"trap_station",
	"trap_instance",
	"species_count",
	"trap_sample",
	"zone",
];

// The tables a spec's per-table `search` record states one box for: the
// workbench's nine, plus the lab records page's `pool` ledger, which is a table
// of the resolver without being a tab of the workbench.
const SPEC_SEARCH_TABLES: readonly MapReviewTable[] = [
	...MAP_REVIEW_TABLES,
	"pool",
];

/**
 * The subset of the shared store a page adopts in one batch when the state comes
 * from outside it — the URL a visit was opened with. The store assigns these
 * directly (`hydrate`) rather than through the per-field setters, each of which
 * resets the page and fires a load.
 */
export interface MapReviewHydratedState {
	spec: MapReviewSpec;
	activeTable: MapReviewTable;
	page: number;
	limit: number;
	sort: MapReviewSort | null;
	drawerOpen: boolean;
}

/** A spec that narrows nothing: no clauses, no zone selection, no buffers and no
 * Feature membership. The default a view opened without state has. */
export function emptyMapReviewSpec(): MapReviewSpec {
	return {
		zones: [],
		temp_zones: [],
		buffer_combine: "union",
		search: {
			site: "",
			feature: "",
			treatment: "",
			observation: "",
			trap_station: "",
			trap_instance: "",
			species_count: "",
			trap_sample: "",
			zone: "",
			pool: "",
		},
		filters: [],
		feature_ids: [],
	};
}

/**
 * A spec read from an untrusted source (a stored analysis, a URL), or null when
 * any part of it is malformed. Whole-state rejection: a spec the server would
 * refuse is never half-restored.
 *
 * `search` is optional — the box is dead state (the per-table search boxes are
 * gone and `ledgerFilter` ignores it), so a spec that carries none reads as all
 * blank rather than being discarded.
 */
export function parseSpec(value: unknown): MapReviewSpec | null {
	if (typeof value !== "object" || value === null || Array.isArray(value)) {
		return null;
	}
	const stored = value as Record<string, unknown>;
	// An absent search is all-empty; a present one must be an object, and each
	// box it states must be a string. A table with no key restores blank.
	const search = stored.search ?? {};
	if (typeof search !== "object" || search === null || Array.isArray(search)) {
		return null;
	}
	const boxes = search as Record<string, unknown>;
	const perTable = emptyMapReviewSpec().search;
	for (const table of SPEC_SEARCH_TABLES) {
		const text = boxes[table];
		if (text === undefined || text === null) continue;
		if (typeof text !== "string") return null;
		perTable[table] = text;
	}
	if (!Array.isArray(stored.filters)) return null;
	if (!stored.filters.every(isFilter)) return null;
	// A spec written before Zones existed carries no zone selection, and the
	// server states "no narrowing" as an absent or empty list; it restores
	// empty rather than discarding the visit. A stored selection the server
	// would refuse (a negative id) is a malformed spec.
	const zones = stored.zones ?? [];
	if (!Array.isArray(zones)) return null;
	if (!zones.every(isNonNegativeInteger)) return null;
	// A spec written before temporary zones existed carries none; it restores
	// empty. A spec that carries one keeps it, geometry and all — the operator
	// asked for the buffer that was drawn, not for whatever the same filters
	// happen to keep today.
	const tempZones = stored.temp_zones ?? [];
	if (!Array.isArray(tempZones)) return null;
	if (!tempZones.every(isTempZoneShape)) return null;
	// How several inside buffers compose: a spec that states neither restores
	// the default union, and an unknown word is malformed exactly as an unknown
	// enum member is.
	const bufferCombine = stored.buffer_combine ?? "union";
	if (bufferCombine !== "union" && bufferCombine !== "intersection")
		return null;
	// An explicit Feature selection is a member set; a spec written before it
	// existed carries none and restores unnarrowed. A negative id is a malformed
	// spec, exactly as a negative zone id is.
	const featureIDs = stored.feature_ids ?? [];
	if (!Array.isArray(featureIDs)) return null;
	if (!featureIDs.every(isNonNegativeInteger)) return null;
	return {
		zones,
		temp_zones: tempZones,
		buffer_combine: bufferCombine,
		search: perTable,
		filters: stored.filters,
		feature_ids: featureIDs,
	};
}

/** Whether a value names a ledger the server resolves: the workbench's nine or
 * the lab's `pool`. Every table-typed field a tier reads is checked with this. */
export function isMapReviewTable(value: unknown): value is MapReviewTable {
	return (
		typeof value === "string" &&
		SPEC_SEARCH_TABLES.some((table) => table === value)
	);
}

export function isSort(value: unknown): value is MapReviewSort {
	if (typeof value !== "object" || value === null || Array.isArray(value)) {
		return false;
	}
	const sort = value as Record<string, unknown>;
	return (
		typeof sort.column === "string" &&
		sort.column !== "" &&
		typeof sort.desc === "boolean"
	);
}

export function isFilter(value: unknown): value is MapReviewColumnFilter {
	if (typeof value !== "object" || value === null || Array.isArray(value)) {
		return false;
	}
	const filter = value as Record<string, unknown>;
	if (!isMapReviewTable(filter.table)) return false;
	if (typeof filter.column !== "string" || filter.column === "") return false;
	if (!isFilterOp(filter.op)) return false;
	switch (filter.op) {
		case "contains":
			return (
				filter.text === null ||
				filter.text === undefined ||
				typeof filter.text === "string"
			);
		case "enum": {
			const values = filter.values;
			if (values === null || values === undefined) return true;
			return (
				Array.isArray(values) &&
				values.every(
					(item) =>
						typeof item === "string" ||
						(typeof item === "number" && Number.isFinite(item)),
				)
			);
		}
		case "numberRange":
			// Absent or null means "unbounded"; anything else must be a number.
			return (
				(filter.min === null ||
					filter.min === undefined ||
					(typeof filter.min === "number" && Number.isFinite(filter.min))) &&
				(filter.max === null ||
					filter.max === undefined ||
					(typeof filter.max === "number" && Number.isFinite(filter.max)))
			);
		case "dateRange":
			return (
				(filter.from === null ||
					filter.from === undefined ||
					typeof filter.from === "string") &&
				(filter.to === null ||
					filter.to === undefined ||
					typeof filter.to === "string")
			);
		case "relation":
			return (
				isMapReviewTable(filter.source) && typeof filter.source_id === "number"
			);
		default:
			return false;
	}
}

export function isTempZoneShape(value: unknown): value is MapReviewTempZone {
	if (typeof value !== "object" || value === null || Array.isArray(value)) {
		return false;
	}
	const zone = value as Record<string, unknown>;
	const geometry = zone.geometry;
	if (typeof zone.name !== "string") return false;
	if (
		typeof geometry !== "object" ||
		geometry === null ||
		Array.isArray(geometry)
	) {
		return false;
	}
	const shape = geometry as Record<string, unknown>;
	if (shape.type !== "MultiPolygon" || !Array.isArray(shape.coordinates))
		return false;
	// The side is optional (absent is inside); a stated side must be a boolean.
	return zone.inside === undefined || typeof zone.inside === "boolean";
}

export function isNonNegativeInteger(value: unknown): value is number {
	return typeof value === "number" && Number.isInteger(value) && value >= 0;
}

export function isPositiveInteger(value: unknown): value is number {
	return typeof value === "number" && Number.isInteger(value) && value > 0;
}

function isFilterOp(value: unknown): value is MapReviewFilterOp {
	return (
		value === "contains" ||
		value === "enum" ||
		value === "numberRange" ||
		value === "dateRange" ||
		value === "relation"
	);
}
